import { FC, lazy, ReactNode, Suspense, useMemo, useState } from 'react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbEye, TbEyeClosed, TbRefresh, TbTrash } from 'react-icons/tb';

import { useNestedResourceAccess } from '@/hooks/useNestedResourceAccess';
import { useResourceQueryState as useQueryState } from '@/hooks/useResourceQueryState';
import { reportQuery } from '@/queries';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import {
    buildReportComponentDatasetQueryConfiguration,
    getReportComponentDatasetQueryStateKey,
    getReportComponentDatasetRequestKey,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import {
    getWorkspaceResourceStatusToggleLabel,
    getWorkspaceResourceTypeLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useReportComponentStore } from '@/stores';
import { useDatasetStore } from '@/stores/database/aliases/datasets/store';
import { ReportComponent } from '@/types';

import { WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

const EditReportComponentModal = lazy(
    () => import('@/shared/components/modals/ResourceActionModals/EditReportComponentModal'),
);

interface ReportComponentActionsDropdownProps {
    component: ReportComponent;
    children: ReactNode;
    onRefresh?: () => Promise<void> | void;
    refreshing?: boolean;
    refreshDisabled?: boolean;
}

const ReportComponentActionsDropdown: FC<ReportComponentActionsDropdownProps> = ({
    component,
    children,
    onRefresh,
    refreshing,
    refreshDisabled,
}): JSX.Element => {
    const [localRefreshing, setLocalRefreshing] = useState(false);

    const [renameVisible, setRenameVisible] = useState(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [editComponentVisible, setEditComponentVisible] = useState(false);
    const resourceTypeLabel = getWorkspaceResourceTypeLabel('report-component');

    const {
        fetchReportComponentById,
        duplicateReportComponent,
        toggleReportComponentLock,
        toggleReportComponentArchive,
    } = useReportComponentStore();
    const queryDatasets = useDatasetStore((state) => state.queryDatasets);
    const [reportId] = useQueryState('report', reportQuery);
    const reportAccess = useNestedResourceAccess('reports', component?.report?.id ?? reportId);
    const queryStateKey = getReportComponentDatasetQueryStateKey(component);
    const datasetRefreshing = useDatasetStore((state) => state.componentQueryState[queryStateKey]?.loading ?? false);
    const queryConfiguration = useMemo(() => buildReportComponentDatasetQueryConfiguration(component), [component]);
    const requestKey = useMemo(
        () => (queryConfiguration ? getReportComponentDatasetRequestKey(queryStateKey, queryConfiguration) : undefined),
        [queryConfiguration, queryStateKey],
    );
    const isRefreshing = refreshing ?? (datasetRefreshing || localRefreshing);

    const handleRefreshReportComponent = async () => {
        if (!component?.id) {
            return;
        }

        setLocalRefreshing(true);

        try {
            if (onRefresh) {
                await onRefresh();

                return;
            }

            if (queryConfiguration) {
                await queryDatasets(queryStateKey, queryConfiguration, requestKey, { force: true });

                return;
            }

            await fetchReportComponentById(component.id);
        } finally {
            setLocalRefreshing(false);
        }
    };

    const handleToggleLock = async () => {
        if (!reportAccess.canManage) {
            return;
        }

        toggleReportComponentLock(component?.id, !component?.locked);
    };

    const handleToggleArchive = async () => {
        if (!reportAccess.canUpdate || component.locked) {
            return;
        }

        toggleReportComponentArchive(component?.id, !component?.archived);
    };

    const handleDuplicateReportComponent = async () => {
        if (!reportAccess.canRead || !reportAccess.canCreateSibling) {
            return;
        }

        duplicateReportComponent(component?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'refresh',
            icon: <TbRefresh size={'1rem'} />,
            label: isRefreshing ? 'Refreshing...' : 'Refresh',
            onClick: handleRefreshReportComponent,
            permission: 'read',
            disabled: refreshDisabled || isRefreshing,
        },
        {
            key: 'edit',
            icon: (
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='1rem'
                    height='1rem'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-cash-edit'
                >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M7 15h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v3' />
                    <path d='M11 19h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v1.25' />
                    <path d='M18.42 15.61a2.1 2.1 0 1 1 2.97 2.97l-3.39 3.42h-3v-3z' />
                </svg>
            ),
            label: 'Edit Component',
            onClick: () => {
                setEditComponentVisible(true);
            },
            permission: 'update',
            disabled: component.locked,
        },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={component?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', component?.locked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'hide',
            icon: component?.archived ? <TbEye size={'1rem'} /> : <TbEyeClosed size={'1rem'} />,
            label: component?.archived ? 'Show' : 'Hide',
            onClick: handleToggleArchive,
            permission: 'update',
            disabled: component.locked,
        },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
            disabled: component.locked,
        },
        {
            key: 'duplicate',
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateReportComponent,
            permission: 'read',
            disabled: !reportAccess.canCreateSibling,
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/components/${component?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Dashboard ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.editedName,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: 'Delete',
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'update',
            disabled: component.locked,
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={reportAccess}
                history={{ resourceId: component.id, resourceType: 'reportComponents', title: component.name }}
                items={items}
                resourceLabel={resourceTypeLabel}
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            {editComponentVisible && (
                <Suspense fallback={null}>
                    <EditReportComponentModal
                        open={editComponentVisible}
                        setOpen={setEditComponentVisible}
                        component={component}
                    />
                </Suspense>
            )}
            <RenameResourceModal
                open={renameVisible}
                setOpen={setRenameVisible}
                resource={component}
                singularResource='report-component'
            />
            <MoveResourceToFolderModal
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
                resource={component}
                singularResource='report-component'
            />
            <DeleteResourceModal
                open={deleteVisible}
                setOpen={setDeleteVisible}
                resource={component}
                singularResource='report-component'
            />
        </>
    );
};

export default ReportComponentActionsDropdown;
